import { Form, FormItem, ReorderableList } from "client/Components";
import {
	AddTilesResponseModel,
	YammerUpdateTileModel,
	YammerDetailsTileModel,
	UpdateYammerTabModel,
	UpdateYammerTabModelWithFeedId,
	updateYammerTile,
	YammerFeedKind
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	doesNotExceedLength,
	isConvertible,
	isNotEmpty,
	isNotNullNorUndefined,
	isPositive
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useState
} from "react";
import { newGuid } from "client_legacy/ndaw/guid";
import { ExistingTile } from "../common";
import { EditTileFromData, initialGenericState } from "../EditGenericTile";
import { EditTileHandle } from "../EditTile";
import { EditTileGeneralFields } from "../GeneralFields";
import { useUpdateTile } from "../hooks/useUpdateTile";
import { EditYammerTabItem } from "./EditYammerTabItem";
import { YammerFeedIdInfoDialog } from "./YammerFeedIdInfoDialog";

const MAX_TAB_NUMBER = 20;

type YammerEditFormData = EditTileFromData<YammerUpdateTileModel>;

const validationBuilder: IValidationConfiguration<YammerEditFormData> = createValidationConfiguration<YammerEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

const tabValidation = validationBuilder.forCollection("tabs")
	.forItems(
		tab => tab.uniqueId
	);

tabValidation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

tabValidation.forScalar("kind")
	.add(
		isNotNullNorUndefined(),
	);

tabValidation.whenType(isUpdateYammerTabModelWithFeedId)
	.forScalar("feedId")
	.add(
		isNotNullNorUndefined(),
		isPositive(),
		isConvertible()
	);

export type EditYammerTileProps = {
	initialModel: YammerDetailsTileModel;
	mode: ExistingTile;
	onIsLoadingChange(isLoading: boolean): void;
	isLoading: boolean;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditYammerTile = forwardRef((props: EditYammerTileProps, ref: Ref<EditTileHandle>): ReactElement => {
	const {
		initialModel,
		mode,
		onIsLoadingChange,
		isLoading,
		onAfterSave,
	} = props;
	const [isInfoDialogOpen, setIsInfoDialogOpen] = useState<boolean>(false);
	const [formData, setFormData] = useState<YammerEditFormData>({
		...initialGenericState(initialModel),
		tabs: initialModel.tabs
	});
	const formHandler: IFormHandler<YammerEditFormData> = useFormHandler(
		formData,
		setFormData,
		validationBuilder
	);

	const { updateHandler } = useUpdateTile<YammerUpdateTileModel>(
		formHandler,
		updateYammerTile,
		isLoading,
		onIsLoadingChange,
		mode,
		onAfterSave,
		cleanCommonTabObject
	);

	useImperativeHandle(
		ref,
		() => ({
			updateHandler
		}),
		[updateHandler]
	);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			setFormData(
				(formState) => {
					const tabsCopy: UpdateYammerTabModel[] = [...formState.tabs];
					const item: UpdateYammerTabModel = tabsCopy.splice(fromIndex, 1)[0];
					tabsCopy.splice(toIndex, 0, item);

					return {
						...formState,
						tabs: tabsCopy
					};
				}
			);
		},
		[]
	);

	const addTabHandler = (): void => {
		const newTab: UpdateYammerTabModel = {
			...createCommonTabObject(),
			kind: YammerFeedKind.AllCompany
		};

		const tabsHandler: IValueInputAttachable<UpdateYammerTabModel[]> = formHandler.for("tabs");

		tabsHandler.reducer
			.push(newTab);

		tabsHandler.validation
			.triggerInputFinished();
	};

	const removeTabHandler = (index: number): void => {
		const tabsHandler: IValueInputAttachable<UpdateYammerTabModel[]> = formHandler.for("tabs");
		if (tabsHandler.value
			.length > 1
		) {
			tabsHandler.reducer
				.removeAt(index);

			tabsHandler.validation
				.triggerInputFinished();
		}
	};

	return (
		<>
			<Form onSubmit="NONE">
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>
				<FormItem
					hasNoMargin
				>
					<ReorderableList onItemMove={itemMoveHandler}>
						{formHandler
							.for("tabs")
							.map((tabHandler, index) => (
								<EditYammerTabItem
									key={tabHandler.value
										.uniqueId}
									tabHandler={tabHandler}
									onRemoveTab={() => removeTabHandler(index)}
									onInfoDialogOpen={() => setIsInfoDialogOpen(true)}
								/>
							))
						}
					</ReorderableList>
					{
						formData.tabs.length < MAX_TAB_NUMBER && (
							<ReorderableList.AddButton
								trackingId="add-tab-button"
								onClick={addTabHandler}
							/>
						)
					}
				</FormItem>
			</Form>
			{isInfoDialogOpen && <YammerFeedIdInfoDialog onClose={() => setIsInfoDialogOpen(false)} />}
		</>
	);
});

EditYammerTile.displayName = "EditYammerTile";

export function isUpdateYammerTabModelWithFeedId(
	updateYammerTabModel: UpdateYammerTabModel
): updateYammerTabModel is UpdateYammerTabModelWithFeedId {
	return updateYammerTabModel.kind === YammerFeedKind.OtherGroup;
};

function createCommonTabObject(): UpdateYammerTabModel {
	const updateYammerTabModelWithFeedId: UpdateYammerTabModel = {
		kind: YammerFeedKind.OtherGroup,
		uniqueId: newGuid(),
		name: "",
		feedId: null
	};

	return updateYammerTabModelWithFeedId;
}

function cleanCommonTabObject(formData: YammerEditFormData): YammerEditFormData{
	return {
		...formData,
		tabs:formData.tabs.map(
			(tab) => {
				if (isUpdateYammerTabModelWithFeedId(tab)){
					return tab;
				} else {
					return {
						kind: tab.kind,
						uniqueId: tab.uniqueId,
						name: tab.name
					};
				}
			}
		)
	};
}